import {
  AfterViewInit, Directive, ElementRef, EventEmitter, HostListener, Input, Output, Renderer2
} from '@angular/core';

export interface BindingPoint {
  x: number;
  y: number;
}

export interface KeyboardFocusedData {
  old: BindingPoint;
  new: BindingPoint;
}

@Directive({
  selector: '[pacKeyboardFocusBinding]'
})
export class KeyboardFocusBindingDirective implements AfterViewInit {
  private defaultScopePrefix = '_keyboardFocusBinding';

  @Input('pacKeyboardFocusBinding') focusBindingKey: string | BindingPoint;
  @Input() focusBindingScopeName = '';
  @Output() keyboardFocused = new EventEmitter<KeyboardFocusedData>();
  private previousFocusKey = 'ArrowUp';
  private nextFocusKey = 'ArrowDown';
  private leftFocusKey = 'ArrowLeft';
  private rightFocusKey = 'ArrowRight';
  private oldPoint: BindingPoint;
  private newPoint: BindingPoint;

  constructor(private elementRef: ElementRef, private renderer2: Renderer2) { }

  ngAfterViewInit() {
    this.renderer2.setAttribute(this.elementRef.nativeElement, this.focusBindingScope, '');
    if (typeof this.focusBindingKey === 'object') {
      this.renderer2.setAttribute(this.elementRef.nativeElement, 'focus-binding-x', (this.focusBindingKey as BindingPoint).x.toString());
      this.renderer2.setAttribute(this.elementRef.nativeElement, 'focus-binding-y', (this.focusBindingKey as BindingPoint).y.toString());
    }
  }

  @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
    if (!e.shiftKey) {
      this.setFocus(e);
    }
  }

  private get focusBindingScope(): string {
    return this.defaultScopePrefix + this.focusBindingScopeName;
  }

  private setFocus(e: KeyboardEvent) {
    const focusNode = this.getFocusNode(e);
    if (focusNode && focusNode !== this.elementRef.nativeElement) {
      e.stopPropagation();
      e.preventDefault();
      focusNode.focus();
      this.keyboardFocused.emit({ old: this.oldPoint, new: this.newPoint });
    }
  }

  private getFocusNode(e: KeyboardEvent): HTMLElement {
    if (typeof this.focusBindingKey === 'string') {
      const nodes = document.querySelectorAll(`[${this.focusBindingScope}][pacKeyboardFocusBinding="${this.focusBindingKey}"]`);
      let index = Array.from(nodes).findIndex((node) => node === this.elementRef.nativeElement);
      this.oldPoint = { x: -1, y: index };
      if ((e.key || e.code) === this.nextFocusKey) {
        index++;
      } else if ((e.key || e.code) === this.previousFocusKey) {
        index--;
      }
      this.newPoint = { x: -1, y: index };

      return nodes[index] as HTMLElement;
    } else {
      let focusBindingX = this.focusBindingKey.x;
      let focusBindingY = this.focusBindingKey.y;
      this.oldPoint = { x: focusBindingX, y: focusBindingY };
      switch (e.key || e.code) {
        case this.previousFocusKey:
          focusBindingY--;
          break;
        case this.nextFocusKey:
          focusBindingY++;
          break;
        case this.leftFocusKey:
          focusBindingX--;
          break;
        case this.rightFocusKey:
          focusBindingX++;
          break;
      }
      this.newPoint = { x: focusBindingX, y: focusBindingY };

      return document.querySelector(`[${this.focusBindingScope}][focus-binding-x="${focusBindingX}"][focus-binding-y="${focusBindingY}"]`);
    }
  }
}
